import React, { useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Coins, RefreshCw, AlertTriangle } from "lucide-react";

export default function CommissionImpactReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/analytics/commission-impact");
      setData(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Report failed.");
    } finally { setBusy(false); }
  };

  const t = data?.totals || {};
  const n = data?.no_membership_transacting || {};
  const g = data?.gated_id_status || {};

  return (
    <div className="mt-4 border border-sniper bg-surface p-5" data-testid="commission-impact-report">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
            <Coins className="w-4 h-4 text-olive-bright" /> Commission / Membership Impact
          </h3>
          <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1 max-w-xl">
            Read-only. How many real accounts have no membership but still transact, and how many
            live listings would become un-buyable if the "no membership → cannot transact" policy
            turned on. Nothing is changed, paused, or cancelled.
          </p>
        </div>
        <button onClick={run} disabled={busy} data-testid="commission-impact-run"
          className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-4 py-2 text-sm transition-colors">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Running…" : (data ? "Re-run" : "Run report")}
        </button>
      </div>

      {data && (
        <div className="mt-4 space-y-4" data-testid="commission-impact-results">
          {/* Membership split */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="border border-sniper px-3 py-2" data-testid="ci-real-accounts">
              <div className="font-heading text-2xl tabular-nums text-foreground">{t.real_accounts ?? 0}</div>
              <div className="font-mono text-[10px] uppercase text-secondary-foreground">Real accounts</div>
            </div>
            <div className="border border-sniper px-3 py-2" data-testid="ci-with-membership">
              <div className="font-heading text-2xl tabular-nums text-olive-bright">{t.with_membership ?? 0}</div>
              <div className="font-mono text-[10px] uppercase text-secondary-foreground">With membership</div>
            </div>
            <div className="border border-sniper px-3 py-2" data-testid="ci-without-membership">
              <div className="font-heading text-2xl tabular-nums text-amber-400">{t.without_membership ?? 0}</div>
              <div className="font-mono text-[10px] uppercase text-secondary-foreground">No membership</div>
            </div>
            <div className="border border-[#e07a5f]/40 px-3 py-2" data-testid="ci-listings-at-risk">
              <div className="font-heading text-2xl tabular-nums text-[#e07a5f]">{n.live_listings_at_risk ?? 0}</div>
              <div className="font-mono text-[10px] uppercase text-secondary-foreground">Live listings at risk</div>
            </div>
          </div>

          {/* No-membership transacting breakdown */}
          <div className="border border-sniper" data-testid="ci-transacting">
            <div className="px-3 py-2 border-b border-sniper font-mono text-[10px] uppercase text-muted-foreground">
              No-membership accounts that currently transact
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 px-3 py-2 font-mono text-[11px] uppercase">
              <span className="text-muted-foreground">Active listings: <span className="text-foreground tabular-nums">{n.with_active_listings ?? 0}</span></span>
              <span className="text-muted-foreground">Any listings: <span className="text-foreground tabular-nums">{n.with_any_listings ?? 0}</span></span>
              <span className="text-muted-foreground">In orders: <span className="text-foreground tabular-nums">{n.in_order_history ?? 0}</span></span>
              <span className="text-muted-foreground">Gated (union): <span className="text-[#e07a5f] tabular-nums">{n.gated_union ?? 0}</span></span>
            </div>
            <div className="px-3 py-2 border-t border-sniper/60 font-mono text-[11px] uppercase flex flex-wrap gap-x-6 gap-y-1">
              <span className="text-muted-foreground">Of gated — id_verified: <span className="text-olive-bright tabular-nums">{g.id_verified ?? 0}</span></span>
              <span className="text-muted-foreground">not verified: <span className="text-amber-400 tabular-nums">{g.not_id_verified ?? 0}</span></span>
            </div>
          </div>

          {/* Detail: no-membership sellers with live listings */}
          <div className="border border-sniper" data-testid="ci-detail">
            <div className="px-3 py-2 border-b border-sniper font-mono text-[10px] uppercase text-muted-foreground">
              No-membership sellers with live listings ({data.detail_active_listing_sellers?.length ?? 0})
            </div>
            {(!data.detail_active_listing_sellers || data.detail_active_listing_sellers.length === 0) ? (
              <p className="font-mono text-[11px] uppercase text-muted-foreground px-3 py-2">None.</p>
            ) : (
              <div className="divide-y divide-sniper/50">
                {data.detail_active_listing_sellers.map((r) => (
                  <div key={r.user_id} className="grid grid-cols-[1.4fr_auto_auto_auto] items-center gap-2 px-3 py-1.5 font-mono text-[11px]" data-testid={`ci-row-${r.username}`}>
                    <span className="text-foreground truncate">@{r.username}{r.role !== "user" ? <span className="ml-1 text-[9px] uppercase text-blaze">{r.role}</span> : null}</span>
                    <span className="text-secondary-foreground tabular-nums text-right">{r.active_listings} live</span>
                    <span className={`uppercase text-[9px] px-1.5 py-0.5 border ${r.identity_verified ? "border-olive/50 text-olive-bright" : "border-amber-400/50 text-amber-400"}`}>{r.identity_verified ? "id ✓" : "no id"}</span>
                    <span className="uppercase text-[9px] text-muted-foreground text-right">{r.in_orders ? "in orders" : "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="flex items-start gap-2 font-mono text-[10px] uppercase text-muted-foreground leading-relaxed" data-testid="ci-note">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
            {data.note}
          </p>
        </div>
      )}
    </div>
  );
}
